import {
  tagSchema,
  noteSchema,
  userSchema,
  blockSchema,
  userSourceSchema,
  instructionSchema,
  type Tag,
} from '@flintmint/core-shared/rxdb';
import { defaultConflictHandler, type RxConflictHandler } from 'rxdb';

// Consulted by the replication downstream ('downstream-check-if-equal-1') right before it
// overwrites the local document with an incoming master state; reporting the states as equal
// skips that write. It keeps an archived pull that was issued before a local write from reviving a
// locally deleted document, re-archiving a locally restored one, or rolling back a local re-archive.
export const partitionConflictHandler: RxConflictHandler<any> = {
  isEqual: (master, fork, context) =>
    (context === 'downstream-check-if-equal-1' && isSupersededMasterState(master, fork)) ||
    defaultConflictHandler.isEqual(master, fork, context),
  // Master-wins is load-bearing for tag counts: see "Push-conflict rollback" in docs/tag-counts/tag-counts-architecture.md.
  resolve: defaultConflictHandler.resolve,
};

type ArchiveState = { _deleted?: boolean; archived?: boolean; archivedAt?: string | null };

const isSupersededMasterState = (master: ArchiveState, fork: ArchiveState): boolean => {
  // A tombstone always applies: a delete made on another device must reach this one.
  if (master._deleted) {
    return false;
  }

  if (fork._deleted) {
    return true;
  }

  return master.archived === true && hasFresherLocalArchive(master, fork);
};

// A restored fork also wins ties: the restore is the newer decision about that archive generation.
// A fork archived again only wins against a strictly older generation, so a same-generation server
// write still applies.
const hasFresherLocalArchive = (master: ArchiveState, fork: ArchiveState): boolean => {
  if (fork.archived === true) {
    return typeof master.archivedAt === 'string' && typeof fork.archivedAt === 'string' && master.archivedAt < fork.archivedAt;
  }

  return fork.archived === false && !isLaterArchive(master, fork);
};

// A fork with no archivedAt was never archived, so any incoming archive is later than it.
const isLaterArchive = (master: ArchiveState, fork: ArchiveState): boolean =>
  typeof master.archivedAt === 'string' && (fork.archivedAt == null || master.archivedAt > fork.archivedAt);

// tags.numDocs is a replicated counter maintained on every device from its own committed writes.
// The default resolver drops the fork on a push conflict, losing whatever a stale device
// accumulated; counter deltas commute, so the correct merge is master + (fork - assumedMaster).
// See docs/tag-counts/tag-counts-architecture.md for the counter contract, the residual drift this cannot cover, and
// the reconcile script that heals it.
export const tagConflictHandler: RxConflictHandler<Tag> = {
  isEqual: partitionConflictHandler.isEqual,
  resolve: async (input, context) => {
    const { assumedMasterState, realMasterState, newDocumentState } = input;

    if (!assumedMasterState || realMasterState._deleted || newDocumentState._deleted || assumedMasterState._deleted) {
      return defaultConflictHandler.resolve(input, context);
    }

    if (
      !Number.isFinite(assumedMasterState.numDocs) ||
      !Number.isFinite(newDocumentState.numDocs) ||
      !Number.isFinite(realMasterState.numDocs)
    ) {
      console.error('[tagConflictHandler] numDocs missing or not finite, falling back to master state', {
        assumedMasterNumDocs: assumedMasterState.numDocs,
        realMasterNumDocs: realMasterState.numDocs,
        forkNumDocs: newDocumentState.numDocs,
      });

      return defaultConflictHandler.resolve(input, context);
    }

    const localDelta = newDocumentState.numDocs - assumedMasterState.numDocs;
    const mergedNumDocs = realMasterState.numDocs + localDelta;

    // Stored as-is even when negative: the merged value is the sum of two exact deltas, and
    // clamping it here would silently discard the part of the delta below zero, which a later
    // increment from either device would then never restore. Display clamps instead. Still
    // logged (Sentry via console capture) because a negative merge means some device's count
    // drifted below the documents it can see.
    if (mergedNumDocs < 0) {
      console.error('[tagConflictHandler] numDocs merge went negative', {
        assumedMasterNumDocs: assumedMasterState.numDocs,
        realMasterNumDocs: realMasterState.numDocs,
        forkNumDocs: newDocumentState.numDocs,
      });
    }

    return {
      ...realMasterState,
      numDocs: mergedNumDocs,
    };
  },
};

export default {
  blocks: {
    schema: blockSchema,
    conflictHandler: partitionConflictHandler,
  },
  notes: {
    schema: noteSchema,
    conflictHandler: partitionConflictHandler,
  },
  userSources: {
    schema: userSourceSchema,
    conflictHandler: partitionConflictHandler,
  },
  users: {
    schema: userSchema,
    conflictHandler: partitionConflictHandler,
  },
  tags: {
    schema: tagSchema,
    conflictHandler: tagConflictHandler,
  },
  instructions: {
    schema: instructionSchema,
    conflictHandler: partitionConflictHandler,
  },
};
